import { Dispatch, SetStateAction, useCallback, useMemo } from "react";
import { ColDef, RowClassParams, RowClickedEvent, RowDoubleClickedEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";

import useUiuxStore from "src/store/useUiuxStore";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";

import { aggridHeaderClass, commonBorderString } from "src/components/common/common";
import { showWarning } from "src/utils/toast/toastUtil";
import { getRowClassRules } from "./SearchTableContent";

// 새로운 구조 import
import { SearchableDto, CodeType } from "src/types/base";
import { getUiConfig, getApiConfig } from "src/utils/categoryUtils";

// ColumnConfig 인터페이스 정의
interface ColumnConfig {
  headerName: string;
  field: string;
  flex?: number;
  width?: number;
  cellRenderer?: string;
  rendererConfig?: {
    type: 'commonCode';
    listKey: string;
    codeField?: string;
    nameField?: string;
  };
}


interface SearchTableProps<T extends SearchableDto> {
	codeType: CodeType;
	formName?: string;
	data: T[];
	selected: T | undefined;
	setSelected: (item: T | undefined) => void;
	onConfirm?: (data: T) => void;
	onCloseConfirm?: (data: T) => void;
	processType_focus?: string;
}

export const SearchTable = <T extends SearchableDto>(props: SearchTableProps<T>) => {
	const { 
		codeType,
		formName,
		data,
		selected,
		setSelected,
		onConfirm = () => { alert('모달 코드 등록후 행동할 동작이 없습니다.'); },
		onCloseConfirm = () => { /* alert('모달 코드 등록후 행동할 동작이 없습니다.'); */ },
		processType_focus = 'normal',
	} = props;

	const { codeSearchModalProps, setCodeSearchModalVisible } = useUiuxStore();
	const commonCodeStore = useCommonCodeStore();

	// 설정에서 컬럼 정의와 코드 필드 가져오기 (새로운 구조)
	const config = useMemo(() => {
		// 공통코드 스토어 값을 전달하여 동적 컬럼 생성
		const storeData = { 
			unitList: commonCodeStore.unitList,
			itemAccountList: commonCodeStore.itemAccountList
		};
		const uiConfig = getUiConfig(codeType, storeData);
		const apiConfig = getApiConfig(codeType);
		
		if (!uiConfig || !apiConfig) {
			console.warn(`Missing config for codeType: ${codeType}`);
			return { columns: [], codeField: '', nameField: '' };
		}
		
		return {
			columns: uiConfig.columns,
			codeField: apiConfig.codeField,
			nameField: apiConfig.nameField
		};
	}, [codeType, commonCodeStore.unitList, commonCodeStore.itemAccountList]);

	// 범용 공통코드 렌더러 생성 함수
	const createCommonCodeRenderer = useCallback((
		list: any[], 
		fieldName: string, 
		codeField: string = 'minorCd', 
		nameField: string = 'codeNm'
	) => {
		return (params: any) => {
			if (!params.data) return '';
			const codeValue = params.data[fieldName];
			if (codeValue === null || codeValue === undefined) return '';
			
			const foundItem = list.find((item: any) => item[codeField] === codeValue);
			return foundItem ? foundItem[nameField] : codeValue;
		};
	}, []);

	// 특별한 셀 렌더러 생성 함수 (방안 3: rendererConfig 기반)
	const createCellRenderer = useCallback((rendererType: string, rendererConfig?: ColumnConfig['rendererConfig'], fieldName?: string) => {
		// 범용 공통코드 렌더러 처리
		if (rendererType === 'commonCodeRenderer' && rendererConfig && fieldName) {
			const { listKey, codeField = 'minorCd', nameField = 'codeNm' } = rendererConfig;
			const list = (commonCodeStore as any)[listKey] || [];
			
			if (list.length > 0) {
				return createCommonCodeRenderer(list, fieldName, codeField, nameField);
			}
		}
		
		// 기타 특수 렌더러는 여기에 추가
		return undefined;
	}, [commonCodeStore, createCommonCodeRenderer]);

	// 컬럼 정의 생성
	const columnDefs: ColDef<any>[] = useMemo(() => {
		return config.columns.map((col: ColumnConfig) => {
			const colDef: ColDef<any> = {
				headerName: col.headerName,
				field: col.field,
				cellStyle: { borderRight: commonBorderString, fontSize: "16px" },
				headerClass: aggridHeaderClass
			};

			// 순번 컬럼 특별 처리
			if (col.field === 'rowIndex') {
				colDef.valueGetter = 'node.rowIndex + 1';
				colDef.width = col.width;
			} else {
				// flex 또는 width 설정
				if (col.flex) {
					colDef.flex = col.flex;
				} else if (col.width) {
					colDef.width = col.width;
				}
			}

			// 특별한 렌더러 설정 (방안 3: rendererConfig 기반)
			if (col.cellRenderer) {
				colDef.cellRenderer = createCellRenderer(col.cellRenderer, col.rendererConfig, col.field);
			}

			return colDef;
		});
	}, [config.columns, createCellRenderer]);

	// 그리드 데이터 생성 (필요한 필드만 추출)
	const gridData = useMemo(() => {
		return data.map((item) => {
			const gridItem: any = {};
			config.columns.forEach((col: ColumnConfig) => {
				if (col.field !== 'rowIndex') {
					gridItem[col.field] = (item as any)[col.field];
				}
			});
			return gridItem;
		});
	}, [data, config.columns]);

	// 행 클릭 핸들러 (함수형 업데이트 사용)
	const handleRowClick = useCallback((e: RowClickedEvent<any>) => {
		const rowData = e.data;
		if (!rowData || rowData[config.codeField] === '') return;

		const foundItem = data.find(item => (item as any)[config.codeField] === rowData[config.codeField]);
		if (!foundItem) return;

		if (selected && (selected as any)[config.codeField] === rowData[config.codeField]) {
			setSelected(undefined);
		} else {
			setSelected(foundItem);
		}
	}, [data, config.codeField, selected, setSelected]);

	// 행 더블클릭 핸들러
	const handleRowDoubleClick = useCallback((e: RowDoubleClickedEvent<any>) => {
		const rowData = e.data;
		if (!rowData || rowData[config.codeField] === '') return;

		const foundData = gridData.find(item => item[config.codeField] === rowData[config.codeField]);
		const searchedItem = data.find(item => (item as any)[config.codeField] === foundData?.[config.codeField]);

		if (!searchedItem) {
			showWarning(`${config.codeField}이(가) 존재하지 않습니다.`);
			return;
		}



		if (onConfirm) onConfirm(searchedItem);
		setCodeSearchModalVisible(false);

		if (onCloseConfirm) onCloseConfirm(selected as T);
	}, [
		gridData, 
		data, 
		config.codeField, 
		codeType, 
		codeSearchModalProps?.isInfo, 
		onConfirm, 
		setCodeSearchModalVisible, 
		onCloseConfirm, 
		selected
	]);

	// 행 클래스 규칙
	const rowClassRules: Record<string, (params: RowClassParams<any>) => boolean> = useMemo(() => {
		return getRowClassRules(selected as any);
	}, [selected]);

	return (
		<div className="ag-theme-alpine w-full h-full max-h-full flex flex-col overflow-y-auto border border-gray-300">
			<AgGridReact
				rowData={gridData}
				columnDefs={columnDefs}
				suppressScrollOnNewData={true}
				defaultColDef={{
					sortable: true,
					resizable: true,
					cellClass: 'border-r-2 border-gray-300',
					hide: false
				}}
				onRowClicked={handleRowClick}
				onRowDoubleClicked={handleRowDoubleClick}
				rowClassRules={rowClassRules}
			/>
		</div>
	);
};
